import { ChangeDetectionStrategy, Component, ElementRef, computed, inject, input, model } from '@angular/core';

export interface TabItem {
  value: string;
  label: string;
  /** Optional count shown after the label (the `.cnt`). */
  count?: number;
}

/**
 * `<nb-tabs>` — the underline tab strip (Mockup `.tabs`).
 *
 * An accessible `tablist` with automatic activation: Left/Right arrows (and
 * Home/End) move focus and selection, roving tabindex keeps a single tab stop.
 * Two-way bind `value`; render the matching `role="tabpanel"` yourself.
 *
 * @example
 * <nb-tabs [tabs]="[{value:'alle',label:'Alle'},{value:'mine',label:'Mine',count:3}]"
 *          [(value)]="filter" ariaLabel="Filtrer saker" />
 */
@Component({
  selector: 'nb-tabs',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    role: 'tablist',
    '[attr.aria-label]': 'ariaLabel() || null',
    '(keydown)': 'onKeydown($event)',
  },
  template: `@for (tab of tabs(); track tab.value; let i = $index) {
    <button
      type="button"
      role="tab"
      class="tab"
      [class.on]="tab.value === value()"
      [attr.aria-selected]="tab.value === value()"
      [attr.tabindex]="tab.value === selectedValue() ? 0 : -1"
      (click)="select(tab.value)"
    >
      {{ tab.label }}
      @if (tab.count !== undefined) {
        <span class="cnt">{{ tab.count }}</span>
      }
    </button>
  }`,
  styleUrl: './tabs.css',
})
export class NbTabs {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly tabs = input.required<TabItem[]>();
  readonly value = model<string>();
  readonly ariaLabel = input<string>();

  protected readonly selectedValue = computed(() => this.value() ?? this.tabs()[0]?.value);
  private readonly selectedIndex = computed(() => {
    const i = this.tabs().findIndex((t) => t.value === this.selectedValue());
    return i < 0 ? 0 : i;
  });

  protected select(value: string): void {
    this.value.set(value);
  }

  protected onKeydown(e: KeyboardEvent): void {
    const tabs = this.tabs();
    if (!tabs.length) return;
    let next = this.selectedIndex();
    switch (e.key) {
      case 'ArrowRight':
        next = (next + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        next = (next - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    this.value.set(tabs[next].value);
    this.host.nativeElement.querySelectorAll<HTMLButtonElement>('button')[next]?.focus();
  }
}
